import { shows, type Show } from "@/mocks/tv/shows";

export interface FilterState {
  genres: string[];
  regions: string[];
  years: string[];
}

export function getShowById(id: string): Show | undefined {
  return shows.find((show) => show.id === id);
}

export function getShowsByType(type: string): Show[] {
  return shows.filter((show) => show.type === type);
}

export function filterShows(list: Show[], filter: Partial<FilterState>): Show[] {
  return list.filter((show) => {
    const genreOk =
      !filter.genres || filter.genres.length === 0 || show.genres.some((g) => filter.genres!.includes(g));
    const regionOk =
      !filter.regions || filter.regions.length === 0 || filter.regions.includes(show.region);
    const yearOk =
      !filter.years || filter.years.length === 0 || filter.years.includes(String(show.year));
    return genreOk && regionOk && yearOk;
  });
}

export function sortShows(list: Show[], sort: string): Show[] {
  const sorted = [...list];
  if (sort === "popular") {
    sorted.sort((a, b) => b.popularity - a.popularity);
  } else if (sort === "year") {
    sorted.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  } else {
    sorted.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  }
  return sorted;
}

export function searchShows(list: Show[], keyword: string): Show[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return list;
  return list.filter((show) => {
    const haystack = [
      show.title,
      show.director,
      show.region,
      show.synopsis,
      ...show.genres,
      ...show.actors,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getLatestShows(list: Show[], limit?: number): Show[] {
  const sorted = sortShows(list, "latest");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getPopularShows(list: Show[], limit?: number): Show[] {
  const sorted = sortShows(list, "popular");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getShowsByTopic(topicId: string, limit?: number): Show[] {
  const matched = shows.filter((show) => show.topics.includes(topicId));
  const sorted = sortShows(matched, "popular");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getRelatedShows(current: Show, limit = 6): Show[] {
  const scored = shows
    .filter((show) => show.id !== current.id)
    .map((show) => {
      let score = 0;
      if (show.type === current.type) score += 2;
      if (show.region === current.region) score += 2;
      score += show.genres.filter((g) => current.genres.includes(g)).length * 3;
      const yearGap = Math.abs(show.year - current.year);
      if (yearGap <= 1) score += 1;
      return { show, score };
    })
    .sort((a, b) => b.score - a.score || b.show.popularity - a.show.popularity);

  return scored.slice(0, limit).map((item) => item.show);
}

export function formatRating(rating: number | null): string {
  return rating === null ? "暂无评分" : rating.toFixed(1);
}

export function formatPopularity(popularity: number): string {
  if (popularity >= 10000) return `${(popularity / 10000).toFixed(1)}万`;
  return String(popularity);
}